import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  error: Error | null
}

/**
 * Generic error boundary. Catches render errors from children and shows a
 * warm, non-technical message rather than a blank screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[FitUTogether] Render error:', error, info)
  }

  render() {
    if (this.state.error) {
      return (
        this.props.fallback ?? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100dvh',
              gap: '1rem',
              padding: '2rem',
              fontFamily: 'Lexend, system-ui, sans-serif',
              color: 'var(--page-text, #2D2D2D)',
              textAlign: 'center',
            }}
          >
            <p style={{ fontSize: '1.1rem', fontWeight: 400 }}>
              Something didn't load correctly.
            </p>
            <pre
              style={{
                fontSize: '0.75rem',
                opacity: 0.5,
                maxWidth: '520px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {this.state.error.message}
            </pre>
            <button
              onClick={() => window.location.reload()}
              style={{
                fontFamily: 'inherit',
                fontSize: '0.9rem',
                padding: '0.6rem 1.5rem',
                border: '1.5px solid var(--page-ring, #B8B2AA)',
                borderRadius: '999px',
                background: 'transparent',
                cursor: 'pointer',
                color: 'inherit',
              }}
            >
              Reload
            </button>
          </div>
        )
      )
    }
    return this.props.children
  }
}
